import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import { useAuth } from './AuthContext';
import { JobFavoriteWithJob } from '../types';

interface FavoritesState {
  favorites: JobFavoriteWithJob[];
  map: Record<string, number>; // job_id (string) -> favorite_id
  loading: boolean;
  refresh: () => Promise<void>;
  add: (jobId: string, notes?: string | null) => Promise<number | null>; // returns favorite_id
  remove: (favoriteId: number) => Promise<boolean>;
}

const FavoritesContext = createContext<FavoritesState | undefined>(undefined);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState<JobFavoriteWithJob[]>([]);
  const [map, setMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(false);

  const buildMap = (items: any[]) => {
    const m: Record<string, number> = {};
    items.forEach((f: any) => {
      // Use job.id from the job object instead of job_id from favorite
      // This ensures MainJob favorites (UUID strings) are correctly mapped
      const jobId = f.job?.id || f.job_id || '';
      if (jobId) {
        m[String(jobId)] = f.id;
      }
    });
    return m;
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const items = await apiService.listFavorites();
      setFavorites(items as any);
      setMap(buildMap(items));
    } catch (e: any) {
      // If unauthorized, keep current state to avoid flicker and let route protection handle access
      if (e?.response?.status === 401) {
        // no-op: keep existing favorites/map
      } else {
        setFavorites([]);
        setMap({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      refresh();
    }
  }, [authLoading, isAuthenticated, refresh]);

  const add = useCallback(async (jobId: string, notes?: string | null) => {
    try {
      const created = await apiService.addFavorite(jobId, notes);
      // Immediately update the map with the new favorite ID using the jobId we passed
      // This ensures the bookmark appears filled right away, even before refresh completes
      setMap((prev) => ({ ...prev, [String(jobId)]: created.id }));
      // Refresh to populate list with full job details (runs async)
      refresh();
      return created.id as number;
    } catch (e: any) {
      console.error('Error adding favorite:', e);
      // Log detailed error for debugging
      if (e?.response?.data?.detail) {
        console.error('API error detail:', e.response.data.detail);
      }
      throw e; // Re-throw to allow caller to handle
    }
  }, [refresh]);

  const remove = useCallback(async (favoriteId: number) => {
    try {
      await apiService.removeFavorite(favoriteId);
      // Find the favorite being removed to get its job ID
      setFavorites((prev) => {
        const favorite = prev.find((f) => f.id === favoriteId);
        if (favorite) {
          const jobId = favorite.job?.id || favorite.job_id || '';
          setMap((mapPrev) => {
            const newMap = { ...mapPrev };
            if (jobId) {
              delete newMap[String(jobId)];
            }
            return newMap;
          });
        }
        return prev.filter((f) => f.id !== favoriteId);
      });
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({ favorites, map, loading, refresh, add, remove }),
    [favorites, map, loading, refresh, add, remove]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
